import EditIcon from "@mui/icons-material/Edit";
import SettingsIcon from "@mui/icons-material/Settings";
import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { Axios } from "api/axios";
import ActionButton from "components/Buttons/ActionButton";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import StatusToggle from "components/StatusToggle";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import { app_modules } from "helpers/apps_modules_list";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  exportWarehouseMasterCSV,
  fetchWarehouseMaster,
} from "slices/Master/Warehouse/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";

const WarehouseMaster = () => {
  document.title = "Warehouse List";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const selectPRs = createSelector(
    (state: any) => state.WarehouseMasterReducer,
    (data) => ({
      list: data.list,
      rowCount: data.rowCount,
      loading: data.loading,
      error: data.error,
    })
  );

  const { list, rowCount, loading, error } = useSelector(selectPRs);

  // Use custom hook to fetch and access permissions
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.master?.app_name,
    app_modules?.master?.modules?.warehouse
  );

  // Use preserved table state hook
  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = usePreservedTableState({
    key: "warehouse",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const [data, setData] = useState([]);
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  useEffect(() => {
    dispatch(
      fetchWarehouseMaster({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    filterValue,
  ]);

  useEffect(() => {
    setData(list);
  }, [list]);

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const trimmedInput = searchInput.trim();

      const dateMatch = trimmedInput.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        const isoDate = `${year}-${month}-${day}`;
        setSearch(isoDate);
      } else {
        setSearch(trimmedInput);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch]);

  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      await exportWarehouseMasterCSV({
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      });

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      console.error("Export error:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export warehouse data";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  };

  const handleStatusToggle = useCallback(
    (row: any) => {
      if (!canUpdate) {
        toast.error("You Dont Have Permission to Change Status");
        return;
      }

      Axios.patch(`/master/warehouse/update-status/${row?.warehouse_id}/`)
        .then((success) => {
          toast.success(
            `Warehouse Status ${row?.is_active ? "Deactivated" : "Activated"}`
          );
          dispatch(
            fetchWarehouseMaster({
              page: pageIndex + 1,
              pageSize,
              sort: sortField,
              order: sortField ? sortDirection : "",
              search,
              filters: filterValue,
            })
          );
        })
        .catch((error) => {
          console.log("Error : ", error);
          toast.error("Something Went Wrong");
        });
    },
    [
      canUpdate,
      dispatch,
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      filterValue,
    ]
  );

  // Create menu items for DynamicMenuItem
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        icon: EditIcon,
        label: "Edit",
        permission: canUpdate,
        onClick: (warehouse: any) =>
          navigate(`/edit-warehouse/${warehouse.warehouse_id}`, {
            state: warehouse,
          }),
        color: "primary",
      },
      {
        icon: SettingsIcon,
        label: "Configure",
        permission: canUpdate,
        onClick: (warehouse: any) =>
          navigate(`/configure-warehouse/${warehouse.warehouse_id}`, {
            state: warehouse,
          }),
        color: "info",
      },
    ];
    return items;
  }, [canUpdate, navigate]);

  const DynamicAction = useMemo(() => {
    // Only show Action column if at least one menu item has permission
    const hasAnyMenuItemPermission = menuItems.some(
      (item) => item.permission === true
    );

    if (!hasAnyMenuItemPermission) return [];

    return [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <DynamicMenuItem row={row.original} menuItems={menuItems} />
        ),
      },
    ];
  }, [menuItems]);

  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        // accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row, table }: { row: any; table: any }) => {
          const pageIndex = table.getState().pagination.pageIndex;
          const pageSize = table.getState().pagination.pageSize;
          const rowIndex = row.index;
          return (
            row.original.sr_no || pageIndex * pageSize + rowIndex + 1 || "-"
          );
        },
      },
      {
        header: "Warehouse Name",
        accessorKey: "warehouse_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/warehouse/dropdown/warehouse_name",
        cell: ({ row }: { row: any }) => {
          return row?.original?.warehouse_name || "-";
        },
      },
      {
        header: "Warehouse Type",
        accessorKey: "warehouse_type",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/warehouse/dropdown/warehouse_type",
        cell: ({ row }: { row: any }) => {
          return row?.original?.warehouse_type || "-";
        },
      },
      {
        header: "Address",
        accessorKey: "address",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/warehouse/dropdown/address",
        cell: ({ row }: { row: any }) => {
          return (
            <TruncatedTextCell
              text={row?.original?.address}
              maxWidth="300px"
              maxLines={4}
              rowIndex={row.index}
              tooltipId={`address-tooltip-${row.index}`}
            />
          );
        },
      },
      {
        header: "City",
        accessorKey: "city",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/warehouse/dropdown/city",
        cell: ({ row }: { row: any }) => {
          return row?.original?.city || "-";
        },
      },
      {
        header: "State",
        accessorKey: "state",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/warehouse/dropdown/state",
        cell: ({ row }: { row: any }) => {
          return row?.original?.state || "-";
        },
      },
      {
        header: "Country",
        accessorKey: "country",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/warehouse/dropdown/country",
        cell: ({ row }: { row: any }) => {
          return row?.original?.country || "-";
        },
      },
      {
        header: "Pincode",
        accessorKey: "pincode",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/warehouse/dropdown/pincode",
        cell: ({ row }: { row: any }) => {
          return row?.original?.pincode || "-";
        },
      },
      {
        header: "Remarks",
        accessorKey: "remark",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          return (
            <TruncatedTextCell
              text={row?.original?.remark}
              maxWidth="300px"
              maxLines={4}
              rowIndex={row.index}
              tooltipId={`remarks-tooltip-${row.index}`}
            />
          );
        },
      },

      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const mod = row.original;
          return (
            <StatusToggle
              isActive={mod.is_active}
              onChange={() => {
                handleStatusToggle(mod);
              }}
              disabled={!canUpdate}
            />
          );
        },
      },

      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user?.username ?? "N/A";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "N/A";
          return `${name}-${date}`;
        },
      },

      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        filterType: "date" as const,
        enableSorting: true,
        // filterType: 'dropdown' as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user?.username ?? "N/A";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "N/A";
          return `${name}-${date}`;
        },
      },

      ...DynamicAction,
    ],
    [DynamicAction, handleStatusToggle, canUpdate]
  );

  return (
    <ListingPageContainer>
      <style>
        {`
          .truncated-text-cell-wrapper {
            min-width: 0 !important;
            display: -webkit-box !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            white-space: normal !important;
            line-height: 1.5em !important;
          }
        `}
      </style>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Warehouse</h6>
                </Col>

                {/* Search Input Box (reusable)*/}
                <Col xl={2} md={3}>
                  <SearchBox value={searchInput} onChange={setSearchInput} />
                </Col>

                <Col className="col-md-auto">
                  <div className="hstack gap-2">
                    {canView && (
                      <ActionButton
                        label="Export"
                        icon="bi bi-box-arrow-up"
                        handleClick={handleExport}
                        variant="light"
                      />
                    )}
                    {canCreate && (
                      <ActionButton
                        label="Add Warehouse"
                        icon="bi bi-plus-circle"
                        navigateLink="/add-warehouse"
                        variant="primary"
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper loading={loading} error={error}>
                <TableContainer
                  columns={columns}
                  data={data}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  colValue={setFilterValue}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  SearchPlaceholder="Search ..."
                  manualSorting={true}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default WarehouseMaster;
